import { Vector3 } from 'three';
import {
  PhysicsParameters,
  TrajectoryPoint,
  TrajectoryData,
  OptimalShotParameters,
  SweetSpotAnalysis
} from '../types';

// Standard cricket physics constants
export const CRICKET_PHYSICS: PhysicsParameters = {
  gravity: 9.81, // m/s²
  airDensity: 1.225, // kg/m³ at sea level, 15°C
  ballMass: 0.163, // kg (cricket ball)
  ballDiameter: 0.073, // meters
  dragCoefficient: 0.47, // sphere
  magnusCoefficient: 0.25, // typical for cricket ball
  coefficientOfRestitution: 0.4 // bat-ball COR
};

/**
 * Calculate trajectory using physics simulation with drag and Magnus effect
 */
export class TrajectoryCalculator {
  private physics: PhysicsParameters;
  
  constructor(physics: PhysicsParameters = CRICKET_PHYSICS) {
    this.physics = physics;
  }

  /**
   * Calculate complete trajectory from shot parameters
   */
  calculateTrajectory(
    exitVelocity: number,
    launchAngle: number,
    horizontalAngle: number,
    spinRate: number,
    timestep: number = 0.01
  ): TrajectoryData {
    const points: TrajectoryPoint[] = [];
    
    // Convert angles to radians
    const launchRad = (launchAngle * Math.PI) / 180;
    const horizontalRad = (horizontalAngle * Math.PI) / 180;
    
    // Initial conditions - ball starts from batsman's position
    // Batsman is at the striker's end (negative Z in our coordinate system)
    const position = new Vector3(0, 0.8, -8); // Ball hit from batsman position at lower height
    
    // For cricket wagon wheel, horizontal angle determines the direction from batsman
    // 0° = straight drive (towards bowler/positive Z)
    // 90° = square leg (positive X - left from batsman's view)
    // -90° = square off/point (negative X - right from batsman's view)
    // 180° = behind wicket keeper (negative Z)
    
    // Calculate velocity components correctly for a proper parabolic trajectory
    const speed = exitVelocity;
    const velocity = new Vector3(
      speed * Math.cos(launchRad) * Math.sin(horizontalRad),  // X: left-right
      speed * Math.sin(launchRad),                            // Y: vertical (upward)
      speed * Math.cos(launchRad) * Math.cos(horizontalRad)   // Z: forward-back
    );
    
    let time = 0;
    let maxHeight = position.y;
    
    // Simulation loop - continue until ball hits ground (y = 0)
    while (position.y >= 0 && time < 10) { // Max 10 seconds flight time (more realistic)
      // Record current state
      points.push({
        position: position.clone(),
        velocity: velocity.clone(),
        time,
        spin: spinRate * (1 - time * 0.05) // Spin decay
      });
      
      // Calculate forces (gravity, drag, Magnus effect)
      const forces = this.calculateForces(velocity, spinRate * (1 - time * 0.05));
      const acceleration = forces.divideScalar(this.physics.ballMass);
      
      // Update velocity and position using integration
      velocity.add(acceleration.multiplyScalar(timestep));
      position.add(velocity.clone().multiplyScalar(timestep));
      
      // Track maximum height
      if (position.y > maxHeight) {
        maxHeight = position.y;
      }
      
      time += timestep;
      
      // Add more points for smoother curve
      if (points.length > 500) break; // Prevent infinite loops
    }
    
    // Ensure we have the landing point at ground level
    if (position.y > 0 && points.length > 0) {
      // Interpolate to find exact ground intersection
      const lastPoint = points[points.length - 1];
      const t = -lastPoint.position.y / velocity.y;
      position.add(velocity.clone().multiplyScalar(t));
      position.y = 0; // Ensure it's exactly at ground level
      
      points.push({
        position: position.clone(),
        velocity: velocity.clone(),
        time: time + t,
        spin: spinRate * (1 - (time + t) * 0.05)
      });
    }
    
    // Calculate landing angle
    const lastPoint = points[points.length - 1];
    const landingAngle = Math.atan2(-lastPoint.velocity.y, 
      Math.sqrt(lastPoint.velocity.x ** 2 + lastPoint.velocity.z ** 2)) * 180 / Math.PI;
    
    // Calculate distance and apply realistic constraints
    const calculatedDistance = Math.sqrt(position.x ** 2 + position.z ** 2);
    const maxRealisticDistance = 120; // meters - typical maximum for cricket sixes
    const constrainedDistance = Math.min(maxRealisticDistance, calculatedDistance);

    return {
      points,
      maxHeight,
      totalDistance: constrainedDistance,
      flightTime: time,
      landingAngle
    };
  }
  
  /**
   * Calculate forces acting on the ball (gravity, drag, Magnus)
   */
  private calculateForces(velocity: Vector3, spinRate: number): Vector3 {
    const speed = velocity.length();
    
    // Gravity force
    const gravity = new Vector3(0, -this.physics.gravity * this.physics.ballMass, 0);
    
    // Drag force - reduced for more realistic cricket ball flight
    const dragMagnitude = 0.5 * this.physics.airDensity * speed ** 2 * 
      this.getDragCoefficient(speed) * this.getBallCrossSection() * 0.3; // Reduced drag effect
    const dragDirection = velocity.clone().normalize().multiplyScalar(-1);
    const drag = dragDirection.multiplyScalar(dragMagnitude);
    
    // Magnus force - for cricket, backspin creates slight lift (positive Y)
    // We'll simplify to only vertical Magnus effect (no side spin)
    const magnusMagnitude = this.physics.magnusCoefficient * this.physics.airDensity *
      this.getBallCrossSection() * speed * (spinRate * 2 * Math.PI / 60) * 0.2; // Reduced effect
    // Magnus force acts upward for backspin
    const magnus = new Vector3(0, magnusMagnitude, 0);
    
    return gravity.add(drag).add(magnus);
  }
  
  /**
   * Get drag coefficient based on Reynolds number
   */
  private getDragCoefficient(_speed: number): number {
    // Simplified drag coefficient for cricket ball
    // Cricket balls have seams which affect drag
    return 0.3; // Typical value for cricket ball with seam
  }
  
  /**
   * Get ball cross-sectional area
   */
  private getBallCrossSection(): number {
    const radius = this.physics.ballDiameter / 2;
    return Math.PI * radius ** 2;
  }
}

/**
 * Calculate optimal shot parameters for maximum distance
 */
export class OptimizationEngine {
  private trajectoryCalc: TrajectoryCalculator;
  
  constructor() {
    this.trajectoryCalc = new TrajectoryCalculator();
  }
  
  /**
   * Find optimal launch parameters for maximum distance
   */
  findOptimalParameters(
    batSpeed: number,
    ballSpeed: number,
    shotType: string
  ): OptimalShotParameters {
    let bestDistance = 0;
    
    // Realistic cricket constraints
    const maxExitVelocity = 55; // m/s - realistic maximum for cricket
    const baseExitVel = Math.min(maxExitVelocity, batSpeed * 1.1); // Conservative improvement
    
    let bestParams: OptimalShotParameters = {
      launchAngle: 25,
      exitVelocity: baseExitVel,
      horizontalAngle: 0,
      spinRate: 2000,
      impactLocation: { x: 0, y: 0 }
    };
    
    // Optimize launch angle (primary factor) - much flatter for cricket
    for (let angle = 5; angle <= 20; angle += 0.5) {
      const exitVel = this.calculateExitVelocity(batSpeed, ballSpeed, angle);
      const trajectory = this.trajectoryCalc.calculateTrajectory(
        exitVel, angle, 0, 2000
      );
      
      if (trajectory.totalDistance > bestDistance) {
        bestDistance = trajectory.totalDistance;
        bestParams.launchAngle = angle;
        bestParams.exitVelocity = exitVel;
      }
    }
    
    // Optimize spin rate
    for (let spin = 1500; spin <= 3000; spin += 100) {
      const trajectory = this.trajectoryCalc.calculateTrajectory(
        bestParams.exitVelocity, bestParams.launchAngle, 0, spin
      );
      
      if (trajectory.totalDistance > bestDistance) {
        bestDistance = trajectory.totalDistance;
        bestParams.spinRate = spin;
      }
    }
    
    // Adjust for shot type
    bestParams = this.adjustForShotType(bestParams, shotType);
    
    return bestParams;
  }
  
  /**
   * Calculate exit velocity from bat and ball speeds using COR
   */
  private calculateExitVelocity(batSpeed: number, ballSpeed: number, launchAngle: number): number {
    // Simplified collision model
    const COR = CRICKET_PHYSICS.coefficientOfRestitution;
    const effectiveBatSpeed = batSpeed * Math.cos(launchAngle * Math.PI / 180);
    
    const calculatedExitVel = COR * (effectiveBatSpeed + ballSpeed) + effectiveBatSpeed;
    
    // Cap at realistic maximum for cricket (world-class batsmen max ~55 m/s)
    const maxExitVelocity = 55; // m/s
    return Math.min(maxExitVelocity, calculatedExitVel);
  }
  
  /**
   * Adjust parameters based on shot type
   */
  private adjustForShotType(params: OptimalShotParameters, shotType: string): OptimalShotParameters {
    const adjusted = { ...params };
    
    switch (shotType.toLowerCase()) {
      case 'drive':
        adjusted.launchAngle = Math.min(adjusted.launchAngle, 20);
        adjusted.horizontalAngle = 0;
        break;
      case 'pull':
        adjusted.horizontalAngle = 45;
        adjusted.launchAngle += 5;
        break;
      case 'sweep':
        adjusted.horizontalAngle = 90;
        adjusted.launchAngle = Math.min(adjusted.launchAngle, 15);
        break;
      case 'cut':
        adjusted.horizontalAngle = -45;
        adjusted.launchAngle = Math.min(adjusted.launchAngle, 10);
        break;
      case 'loft':
        adjusted.launchAngle = Math.max(adjusted.launchAngle, 30);
        break;
    }
    
    return adjusted;
  }
}

/**
 * Analyze sweet spot impact and efficiency
 */
export class SweetSpotAnalyzer {
  private readonly SWEET_SPOT_CENTER = { x: 0, y: 0.15 }; // 15cm from handle
  private readonly SWEET_SPOT_RADIUS = 0.03; // 3cm radius
  
  /**
   * Analyze impact location relative to sweet spot
   */
  analyzeSweetSpot(impactX: number, impactY: number): SweetSpotAnalysis {
    const distance = Math.sqrt(
      (impactX - this.SWEET_SPOT_CENTER.x) ** 2 +
      (impactY - this.SWEET_SPOT_CENTER.y) ** 2
    );
    
    const efficiency = Math.max(0, 1 - (distance / this.SWEET_SPOT_RADIUS));
    
    let recommendation = '';
    if (distance > this.SWEET_SPOT_RADIUS) {
      if (impactY < this.SWEET_SPOT_CENTER.y) {
        recommendation = 'Impact too close to handle - aim higher on bat';
      } else {
        recommendation = 'Impact too close to tip - aim lower on bat';
      }
      
      if (Math.abs(impactX) > 0.01) {
        recommendation += `, and ${impactX > 0 ? 'left' : 'right'} of center`;
      }
    } else {
      recommendation = 'Excellent sweet spot contact!';
    }
    
    return {
      center: this.SWEET_SPOT_CENTER,
      radius: this.SWEET_SPOT_RADIUS,
      actualImpact: { x: impactX, y: impactY },
      distance,
      efficiency,
      recommendation
    };
  }
}

/**
 * Calculate energy transfer efficiency
 */
export function calculateEnergyTransfer(
  batSpeed: number,
  ballSpeed: number,
  exitVelocity: number
): number {
  // Kinetic energy calculations
  const ballMass = CRICKET_PHYSICS.ballMass;
  const batMass = 1.2; // kg (typical bat weight)
  
  const initialBallEnergy = 0.5 * ballMass * ballSpeed ** 2;
  const initialBatEnergy = 0.5 * batMass * batSpeed ** 2;
  const finalBallEnergy = 0.5 * ballMass * exitVelocity ** 2;
  
  const energyTransferred = finalBallEnergy - initialBallEnergy;
  const maxPossibleTransfer = initialBatEnergy;
  
  return Math.min(1, energyTransferred / maxPossibleTransfer);
}

/**
 * Calculate optimal impact timing window
 */
export function calculateTimingWindow(ballSpeed: number): { early: number; perfect: number; late: number } {
  // Timing window in milliseconds
  const baseWindow = 20; // 20ms for perfect timing
  const speedFactor = ballSpeed / 35; // Normalize to typical pace bowling
  
  return {
    early: -baseWindow * speedFactor,
    perfect: 0,
    late: baseWindow * speedFactor
  };
}